import React, { useState } from 'react'
import { Button } from './button'
import { ColumnType } from '../../lib/smartFormatter'

interface FormatContextMenuProps {
  columnType: ColumnType
  onFormatChange: (updates: Partial<ColumnType>) => void
  onClose: () => void
  position: { x: number; y: number }
}

const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
]

const DATE_FORMATS = [
  { key: 'short', label: 'Mar 15, 2020', options: { year: 'numeric', month: 'short', day: 'numeric' } },
  { key: 'long', label: 'March 15, 2020', options: { year: 'numeric', month: 'long', day: 'numeric' } },
  { key: 'numeric', label: '3/15/2020', options: { year: 'numeric', month: 'numeric', day: 'numeric' } },
  { key: 'iso', label: '2020-03-15', options: { year: 'numeric', month: '2-digit', day: '2-digit' } },
  { key: 'year-month', label: 'Mar 2020', options: { year: 'numeric', month: 'short' } },
  { key: 'year-only', label: '2020', options: { year: 'numeric' } },
]

export function FormatContextMenu({ columnType, onFormatChange, onClose, position }: FormatContextMenuProps) {
  const [selectedCurrency, setSelectedCurrency] = useState(columnType.currency || 'USD')
  const [selectedDecimals, setSelectedDecimals] = useState(columnType.decimals || 2)
  const [selectedDateFormat, setSelectedDateFormat] = useState(columnType.dateFormat || 'short')

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency)
    onFormatChange({ currency, decimals: selectedDecimals })
  }

  const handleDecimalsChange = (decimals: number) => {
    setSelectedDecimals(decimals)
    onFormatChange({ decimals, currency: selectedCurrency })
  }

  const handleDateFormatChange = (format: string) => {
    setSelectedDateFormat(format)
    onFormatChange({ dateFormat: format })
  }

  const renderCurrencyOptions = () => (
    <div className="py-2">
      <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
        Currency
      </div>
      <div className="max-h-48 overflow-y-auto">
        {CURRENCY_OPTIONS.map(({ code, symbol, name }) => (
          <button
            key={code}
            onClick={() => handleCurrencyChange(code)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
              selectedCurrency === code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="font-mono">{symbol}</span>
              <span>{name}</span>
            </span>
            <span className="text-xs text-gray-500">{code}</span>
          </button>
        ))}
      </div>
      <div className="px-3 py-2 border-t border-gray-100">
        <div className="text-xs font-medium text-gray-500 mb-2">Decimal Places</div>
        <div className="flex gap-1">
          {[0, 1, 2].map(decimals => (
            <button
              key={decimals}
              onClick={() => handleDecimalsChange(decimals)}
              className={`px-2 py-1 text-xs rounded ${
                selectedDecimals === decimals 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {decimals}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const renderDateOptions = () => (
    <div className="py-2">
      <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
        Date Format
      </div>
      <div className="max-h-48 overflow-y-auto">
        {DATE_FORMATS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleDateFormatChange(key)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
              selectedDateFormat === key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
            }`}
          >
            <span>{label}</span>
            {selectedDateFormat === key && (
              <div className="w-2 h-2 bg-blue-600 rounded-full" />
            )}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-30" 
        onClick={onClose}
      />
      
      {/* Context Menu */}
      <div
        className="fixed z-40 w-64 bg-white border border-gray-200 rounded-md shadow-lg"
        style={{
          left: Math.min(position.x, window.innerWidth - 256),
          top: Math.min(position.y, window.innerHeight - 300),
        }}
      >
        {columnType.type === 'currency' && renderCurrencyOptions()}
        {columnType.type === 'date' && renderDateOptions()}
      </div>
    </>
  )
}